import React, { SyntheticEvent } from 'react';

import CallSplitIcon from '@mui/icons-material/CallSplit';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';

import RoomMatchSuggestions from '~/components/Bedbank/Properties/Edit/RoomMapping/RoomMatchSuggestions';
import RoomSourceCommentModal from '~/components/Bedbank/Properties/Edit/RoomMapping/RoomSourceCommentModal';

import type { AutomappingFlagToggleTarget, PropertyRoom, SourceRoom, SourceRoomGroup } from './types';
import { isSourceRoomGroup } from './types';
import { generateOccupancyText, generateRoomSizeText, generateViewsText } from './util';

/**
 * Highlights matching text in a string by wrapping it in a styled span
 */
const highlightText = (text: string, searchTerm: string): React.ReactNode => {
  if (!searchTerm || !searchTerm.trim()) {
    return text;
  }

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const lowerText = text.toLowerCase();
  const startIndex = lowerText.indexOf(normalizedSearch);

  if (startIndex === -1) {
    return text;
  }

  const endIndex = startIndex + normalizedSearch.length;
  return (
    <>
      {text.substring(0, startIndex)}
      <Box
        component="span"
        sx={{
          backgroundColor: 'warning.light',
          color: 'warning.contrastText',
          fontWeight: 'bold',
          px: 0.5,
          borderRadius: 0.5,
        }}
      >
        {text.substring(startIndex, endIndex)}
      </Box>
      {text.substring(endIndex)}
    </>
  );
};

interface Props {
  room: SourceRoom | SourceRoomGroup;
  isSelected: boolean;
  supplier: App.Bedbank.Suppliers;
  onSelect: (room: SourceRoom | SourceRoomGroup, supplier: App.Bedbank.Suppliers) => void;
  automappingFlagPayload: App.Bedbank.SupplierRoomAutomappingFlagPayload;
  handleSetAutomappingFlags: (targets: AutomappingFlagToggleTarget | Array<AutomappingFlagToggleTarget>) => void;
  candidateRooms?: Array<PropertyRoom>;
  onUngroupRoom: (supplier: App.Bedbank.Suppliers, supplierRoomName: string) => void;
  onQuickMap: (
    sourceRoom: SourceRoom,
    targetPropertyRoomName: string,
    roomGroup?: SourceRoom | SourceRoomGroup,
  ) => void;
  appliedButNotSavedRooms: Array<SourceRoom>;
  isQuickMapping?: boolean;
  onUpdateMLMatchScore: (sourceRoom: SourceRoom, bestScore: number) => void;
  searchTerm?: string;
}

const SourceRoomElement = React.memo(function SourceRoomElement({
  room,
  isSelected,
  supplier,
  onSelect,
  automappingFlagPayload,
  handleSetAutomappingFlags,
  candidateRooms,
  onUngroupRoom,
  onQuickMap,
  appliedButNotSavedRooms,
  isQuickMapping,
  onUpdateMLMatchScore,
  searchTerm,
}: Props) {
  const isGroup = isSourceRoomGroup(room);
  const referenceRoom = isGroup ? room.rooms[0] : room;

  const description = referenceRoom?.description || 'N/A';
  const supplierAndExternalId = isGroup
    ? `(${room.supplier}) ${room.rooms.map((r) => r.externalId).join(', ')}`
    : `(${supplier}) ${referenceRoom.externalId}`;

  const views = generateViewsText(referenceRoom);
  const roomSizeText = generateRoomSizeText(referenceRoom);
  const { occupancyAge, occupancyAllowed } = generateOccupancyText(referenceRoom);

  const computeAutomappingState = () => {
    if (!isGroup) {
      const hasOverride = automappingFlagPayload.some(
        (payload) => payload.supplierRoomId === room.externalId && payload.supplier === room.supplier,
      );

      return {
        checked: room.isAutoMappingEnabled !== hasOverride,
        hasOverride,
      };
    }

    const childStates = room.rooms.map((child) => {
      const hasOverride = automappingFlagPayload.some(
        (payload) => payload.supplierRoomId === child.externalId && payload.supplier === child.supplier,
      );

      return {
        checked: child.isAutoMappingEnabled !== hasOverride,
        hasOverride,
      };
    });

    const checked = childStates.every((state) => state.checked);
    const hasOverride = childStates.some((state) => state.hasOverride);

    return {
      checked,
      hasOverride,
    };
  };

  const automappingState = computeAutomappingState();

  // Check if this source room was quick-mapped (with supplier check to prevent false positives)
  const wasQuickMapped = appliedButNotSavedRooms.some(
    (sr) => sr.externalId === referenceRoom.externalId && sr.supplier === referenceRoom.supplier,
  );

  const handleAutomappingToggle = (_event: SyntheticEvent, checked: boolean) => {
    if (!isGroup) {
      handleSetAutomappingFlags({
        supplierRoomId: room.externalId,
        externalPropertyId: room.externalPropertyId,
        supplier,
        isAutoMappingEnabled: checked,
      });
      return;
    }

    handleSetAutomappingFlags(
      room.rooms.map((childRoom) => ({
        supplierRoomId: childRoom.externalId,
        externalPropertyId: childRoom.externalPropertyId,
        supplier: childRoom.supplier,
        isAutoMappingEnabled: checked,
      })),
    );
  };

  const handleSelectionToggle = () => {
    onSelect(room, supplier);
  };

  const handleUngroupClick = () => {
    if (isGroup) {
      onUngroupRoom(room.supplier, room.supplierRoomName);
    }
  };

  const groupLabel = isGroup ? room.supplierRoomName : referenceRoom?.name ?? referenceRoom?.description;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        backgroundColor: !isSelected ? 'grey.100' : undefined,
        borderColor: isSelected ? 'info.light' : undefined,
      }}
    >
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" justifyContent="space-between">
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <FormControlLabel
            checked={isSelected}
            onChange={handleSelectionToggle}
            control={<Checkbox />}
            label={highlightText(groupLabel, searchTerm ?? '')}
            componentsProps={{ typography: { variant: 'h6' } }}
          />
          {wasQuickMapped && <Chip label="1 click map" color="secondary" size="small" />}
          {isGroup && (
            <Chip
              label={`${room.rooms.length} ${room.rooms.length === 1 ? 'room' : 'rooms'}`}
              color="info"
              size="small"
            />
          )}
        </Stack>
        {isGroup && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<CallSplitIcon />}
            onClick={handleUngroupClick}
            color="primary"
          >
            Split
          </Button>
        )}
      </Stack>

      <Typography>
        <strong>Supplier | External ID:</strong>{' '}
        {isGroup ? (
          <>
            ({room.supplier}){' '}
            {room.rooms.map((r, idx) => (
              <React.Fragment key={r.externalId}>
                {idx > 0 && ', '}
                {highlightText(r.externalId, searchTerm ?? '')}
              </React.Fragment>
            ))}
          </>
        ) : (
          <>
            ({supplier}) {highlightText(referenceRoom.externalId, searchTerm ?? '')}
          </>
        )}
      </Typography>
      {!!referenceRoom?.hasInfo && (
        <Stack direction="column" gap={1}>
          <div>
            <Typography>
              <strong>Views:</strong> {views}
            </Typography>
            <Typography>
              <strong>Room Size:</strong> {roomSizeText}
            </Typography>
            <Typography>
              <strong>Bed Groups:</strong> {referenceRoom?.bedGroups?.map((x) => x.description).join(', ') || 'N/A'}
            </Typography>
            <Typography>
              <strong>Occupancy Age:</strong> {occupancyAge}
            </Typography>
            <Typography>
              <strong>Occupancy Allowed:</strong> {occupancyAllowed}
            </Typography>
            <Typography>
              <strong>Description:</strong> {highlightText(description, searchTerm ?? '')}
            </Typography>
            <Typography>
              <strong>Availability Score:</strong> {referenceRoom?.availabilityScore ?? 'N/A'}
            </Typography>
            <FormControlLabel
              control={<Switch checked={automappingState.checked} />}
              label="Is Automapping Enabled"
              onChange={handleAutomappingToggle}
            />
          </div>
          <Stack direction="column">
            {referenceRoom?.amenities?.length > 0 && (
              <Accordion>
                <AccordionSummary>Amenities</AccordionSummary>
                <AccordionDetails>
                  <Box component="ul" m={0}>
                    {referenceRoom.amenities.map((amenity) => (
                      <li key={amenity}>{amenity}</li>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </Stack>
          <div>
            {referenceRoom?.characteristic && (
              <Typography>
                <strong>Characteristic:</strong> {referenceRoom.characteristic}
              </Typography>
            )}
            {referenceRoom?.occupancy && (
              <Typography>
                <strong>Occupancy:</strong> {referenceRoom.occupancy?.max_allowed.total}
              </Typography>
            )}
          </div>
        </Stack>
      )}
      {candidateRooms && candidateRooms.length > 0 && (
        <RoomMatchSuggestions
          sourceRoom={referenceRoom}
          candidateRoomNames={candidateRooms}
          onQuickMap={(sourceRoom, targetRoomName) => onQuickMap(sourceRoom, targetRoomName, room)}
          isQuickMapping={isQuickMapping}
          onUpdateMLMatchScore={onUpdateMLMatchScore}
        />
      )}
      <RoomSourceCommentModal room={referenceRoom} />
    </Paper>
  );
});

export default SourceRoomElement;
